import React from "react";
import { Button } from "antd";
import { PlayCircle } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../design-system/tokens";
import { card } from "../../../../pages/enduser/ssh-proxy/sshProxyUi";

// Shared chrome for the Add Endpoint flows, so Linux and Windows read like the
// gateway onboarding wizard rather than like two other products.
//
// The primitives themselves (card, CodeBlock, CopyButton, Note, InlineValue)
// come from the SSH proxy wizard — they are generic, and duplicating them here
// is how the two flows drifted apart in the first place.

// `embedded` drops the page chrome so the same component can render inside a
// drawer. Both flows stay routable — the routes are what existing deep links
// (and the onboarding tour) still point at.
export function OnboardShell({ eyebrow, title, subtitle, onClose, closeLabel = "Close", embedded, children }) {
  const pageStyle = embedded
    ? { color: AN_COLORS.ink, fontFamily: AN_FONT }
    : {
        minHeight: "calc(100vh - 64px)",
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        padding: "22px 28px",
      };
  return (
    <div style={pageStyle}>
      {!embedded && (
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6 }}>
            {eyebrow}
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>{title}</h1>
          {subtitle && (
            <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4, maxWidth: 680, lineHeight: 1.5 }}>{subtitle}</div>
          )}
        </div>
        {onClose && <Button onClick={onClose}>{closeLabel}</Button>}
      </div>
      )}

      <div style={{ display: "grid", gap: 16, maxWidth: embedded ? "none" : 900 }}>{children}</div>
    </div>
  );
}

/** Heading for one part of the flow, with an optional tutorial link. */
export function PartHead({ part, title, desc, tutorialUrl }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
      <div>
        <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6 }}>
          {part}
        </div>
        <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 600, color: AN_COLORS.ink, letterSpacing: -0.2 }}>{title}</h3>
        {desc && <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginTop: 4, maxWidth: 620, lineHeight: 1.5 }}>{desc}</div>}
      </div>
      {tutorialUrl && (
        <Button type="link" href={tutorialUrl} target="_blank" rel="noreferrer" style={{ paddingRight: 0 }}>
          <PlayCircle size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          Watch tutorial
        </Button>
      )}
    </div>
  );
}

/** A numbered step inside a part: title, optional blurb, then its content. */
export function StepRow({ n, title, desc, children, last }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "26px 1fr", gap: 12, paddingBottom: last ? 0 : 16 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <span
          style={{
            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 700, fontFamily: AN_MONO,
            background: AN_COLORS.tint, color: AN_COLORS.ink2, border: `1px solid ${AN_COLORS.line}`,
          }}
        >
          {n}
        </span>
        {!last && <span style={{ flex: 1, width: 1, background: AN_COLORS.line, marginTop: 6 }} />}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: AN_COLORS.ink, marginBottom: desc || children ? 6 : 0 }}>{title}</div>
        {desc && <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginBottom: children ? 8 : 0, lineHeight: 1.5 }}>{desc}</div>}
        {children}
      </div>
    </div>
  );
}

export { card };
